import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, isolate } from '@castfeeds/i18n';
import { useT } from '@/i18n';
import { ErrorBox, Spinner } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { logError, logInfo } from '@/lib/log';
import { useSession } from '@/store/session';
import { AuthLayout } from './AuthLayout';
import { LoginForm } from './LoginPage';
import { RegisterForm } from './RegisterPage';

/** /invite/:token — preview the invite, then accept it with a new or an existing account. */
export function InvitePage() {
  const { token = '' } = useParams();
  const { t, td, locale } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const [mode, setMode] = useState<'register' | 'login'>('register');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const preview = useQuery({ queryKey: qk.invitePreview(token), queryFn: () => api.team.previewInvite(token), retry: false });

  const enter = async (workspaceId: string) => {
    const profile = await api.auth.me();
    useSession.getState().setProfile(profile);
    useSession.getState().setWorkspace(workspaceId);
    qc.removeQueries({ queryKey: ['ws'] });
    logInfo('auth', `joined workspace=${workspaceId}`);
    navigate('/', { replace: true });
  };

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.team.acceptInvite(token);
      await enter(res.workspaceId);
    } catch (e) {
      logError('auth', 'accept invite failed', e);
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (preview.isLoading) {
    return <AuthLayout title={t('loading')}><Spinner label={t('loading')} /></AuthLayout>;
  }
  if (preview.error || !preview.data) {
    return (
      <AuthLayout title={t('inviteInvalid')} sub={t('inviteInvalidSub')}>
        <Link to="/login" className="btn">{t('loginBtn')}</Link>
      </AuthLayout>
    );
  }

  const inv = preview.data;
  const role = td(`role_${inv.role}`, inv.role);
  const sameEmail = session.user?.email.toLowerCase() === inv.email.toLowerCase();
  return (
    <AuthLayout
      title={t('inviteTitle', { workspace: inv.workspaceName })}
      sub={
        <>
          {t('inviteAs', { role })} {t('inviteFor', { email: isolate(inv.email) })} {t('inviteExpires', { date: formatDate(locale, inv.expiresAt) })}
        </>
      }
    >
      {error ? <ErrorBox error={error} /> : null}
      {session.token && session.user ? (
        sameEmail ? (
          <div className="stack" style={{ gap: 12 }}>
            <span className="muted small">{t('inviteLoggedInAs', { email: session.user.email })}</span>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={accept}>{busy ? t('inviteAccepting') : t('inviteAccept')}</button>
          </div>
        ) : (
          <div className="stack" style={{ gap: 12 }}>
            <div className="alert alert-warn">{t('inviteWrongEmail', { email: inv.email })}</div>
            <button type="button" className="btn" onClick={() => session.logout()}>{t('logout')}</button>
          </div>
        )
      ) : mode === 'register' ? (
        <>
          <RegisterForm inviteToken={token} email={inv.email} onDone={(s) => void enter(s.memberships[s.memberships.length - 1]?.workspaceId ?? '')} />
          <button type="button" className="btn-link" style={{ justifySelf: 'start' }} onClick={() => setMode('login')}>{t('inviteHaveAccount')}</button>
        </>
      ) : (
        <>
          <LoginForm email={inv.email} onDone={() => void accept()} />
          <button type="button" className="btn-link" style={{ justifySelf: 'start' }} onClick={() => setMode('register')}>{t('inviteNewAccount')}</button>
        </>
      )}
    </AuthLayout>
  );
}
